import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, RotateCcw, ChevronRight } from "lucide-react";
import { PageHero, Section, Eyebrow, H2, P } from "@/components/SharedUI";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Framer Motion variants (hoisted to avoid per-render object allocation).
const STEP_MOTION = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.3 },
};

const QUESTIONS = [
  {
    id: "stage",
    prompt: "Where is your project right now?",
    options: [
      ["Just an idea / scoping", 1],
      ["Approved, in planning", 2],
      ["Underway, hitting governance friction", 3],
      ["In delivery, cultural risk surfacing", 4],
    ],
  },
  {
    id: "authority",
    prompt: "What's the state of cultural authority on this project?",
    options: [
      ["Unclear / not yet identified", 1],
      ["Identified, but no formal sign-off pathway", 2],
      ["Engaged, but ad-hoc and undocumented", 3],
      ["Mapped, with decision rights documented", 4],
    ],
  },
  {
    id: "consent",
    prompt: "How is consent currently recorded?",
    options: [
      ["Verbal / undocumented", 1],
      ["Email or meeting minutes", 2],
      ["Signed forms held by the project team", 3],
      ["Formal FPIC workflow with evidence trail", 4],
    ],
  },
  {
    id: "data",
    prompt: "Where does community or cultural data live?",
    options: [
      ["Personal devices, shared drives or contractor systems", 1],
      ["Agency standard systems", 2],
      ["Controlled workspace, role-based access", 3],
      ["Sovereign infrastructure with custodianship recorded", 4],
    ],
  },
  {
    id: "breach",
    prompt: "What happens if material is shared outside agreed conditions?",
    options: [
      ["Not sure — no process exists", 1],
      ["Ad-hoc, case by case", 2],
      ["Documented escalation, but slow", 3],
      ["Formal escalation pathway with named owners", 4],
    ],
  },
  {
    id: "evidence",
    prompt: "If asked tomorrow, can you produce evidence of cultural sign-off for a board?",
    options: [
      ["No", 1],
      ["Some, scattered across systems", 2],
      ["Most, in different formats", 3],
      ["Yes — a single auditable pack", 4],
    ],
  },
];

const TIERS = [
  {
    band: [6, 11],
    score: "Foundation needed",
    tier: "Toolkit Starter",
    tierLink: "/contact",
    summary:
      "Your project is exposed. Governance is mostly informal, cultural authority is unmapped, and there is no audit trail you could put in front of a board today. Start by building the foundations — diagnostic, authority register, consent and IDS templates, a 30/60/90 day roadmap.",
    cta: "Request Toolkit Starter",
  },
  {
    band: [12, 16],
    score: "Architecture needed",
    tier: "Governance Architecture Pack",
    tierLink: "/architecture",
    summary:
      "You have the right intent, but the operating model is fragmented. Cultural authority exists but is not yet operational. The next move is governance architecture: authority maps, decision-rights matrices, FPIC workflows and an executive evidence pack.",
    cta: "Move to Architecture Pack",
  },
  {
    band: [17, 20],
    score: "Pilot-ready",
    tier: "Protected Pilot",
    tierLink: "/contact",
    summary:
      "You have the foundations in place and the governance architecture is taking shape. You are ready to prove the model in a controlled environment — a protected pilot with live consent and ICIP workflows, audit evidence, and a clear path to scale.",
    cta: "Request a Protected Pilot",
  },
  {
    band: [21, 24],
    score: "Enterprise-scale",
    tier: "Enterprise / Government Licence",
    tierLink: "/pricing",
    summary:
      "Your project operates at scale or you are responsible for governance across multiple agencies, programs or communities. You need a licensed platform with assurance reporting, training, governance onboarding and a renewable cultural authority cycle.",
    cta: "Explore enterprise licensing",
  },
];

function tierFor(score) {
  return TIERS.find(({ band }) => score >= band[0] && score <= band[1]) || TIERS[0];
}

export default function Readiness() {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const allAnswered = QUESTIONS.every((q) => answers[q.id] != null);
  const score = Object.values(answers).reduce((a, b) => a + b, 0);
  const maxScore = QUESTIONS.length * 4;

  const handleSelect = (qid, value) => setAnswers((a) => ({ ...a, [qid]: value }));
  const handleReset = () => {
    setAnswers({});
    setSubmitted(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const tier = submitted ? tierFor(score) : null;

  return (
    <>
      <PageHero
        eyebrow="Self-assessment"
        title="How protected is your project — really?"
        desc="A short, honest read on where your project sits across authority, consent, data sovereignty and audit evidence. Six questions, two minutes, no email required."
      />

      <section className="border-b border-stone-200">
        <Section>
          <AnimatePresence mode="wait">
            {!submitted ? (
              <motion.div
                key="quiz"
                {...STEP_MOTION}
                data-testid="readiness-quiz"
              >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                  <aside className="lg:col-span-3">
                    <div className="lg:sticky lg:top-28">
                      <Eyebrow>Your read</Eyebrow>
                      <div className="font-display text-5xl tracking-tighter font-bold text-stone-900" data-testid="readiness-progress">
                        {Object.keys(answers).length}<span className="text-stone-300">/{QUESTIONS.length}</span>
                      </div>
                      <p className="mt-3 text-sm text-stone-600 leading-relaxed max-w-xs">
                        Answer honestly. The point is the diagnosis, not the score.
                      </p>
                      <div className="mt-6 border border-[#9C4221]/30 bg-[#9C4221]/[0.06] p-4 max-w-xs">
                        <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#9C4221] mb-1">
                          Confidential
                        </div>
                        <p className="text-xs text-stone-700 leading-relaxed">
                          Nothing is sent anywhere. This runs in your browser only.
                        </p>
                      </div>
                    </div>
                  </aside>

                  <div className="lg:col-span-9 space-y-10">
                    {QUESTIONS.map((q, i) => (
                      <fieldset
                        key={q.id}
                        data-testid={`readiness-question-${q.id}`}
                        className="border border-stone-200 bg-[#FAFAF9] p-6 md:p-8"
                      >
                        <legend className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#9C4221] px-2">
                          Question {i + 1} of {QUESTIONS.length}
                        </legend>
                        <h3 className="font-display text-xl md:text-2xl tracking-tight font-bold text-stone-900 leading-snug">
                          {q.prompt}
                        </h3>
                        <div className="mt-5 grid gap-2">
                          {q.options.map(([label, value]) => {
                            const active = answers[q.id] === value;
                            return (
                              <button
                                key={label}
                                type="button"
                                onClick={() => handleSelect(q.id, value)}
                                data-testid={`readiness-option-${q.id}-${value}`}
                                className={`text-left border px-4 py-3.5 text-sm transition-colors flex items-center justify-between gap-3 ${
                                  active
                                    ? "border-stone-900 bg-stone-900 text-white"
                                    : "border-stone-300 text-stone-800 hover:border-stone-900"
                                }`}
                              >
                                <span>{label}</span>
                                {active && <ChevronRight className="h-4 w-4 shrink-0" />}
                              </button>
                            );
                          })}
                        </div>
                      </fieldset>
                    ))}

                    <div className="flex items-center justify-between gap-4">
                      <p className="text-xs text-stone-500 max-w-sm leading-relaxed">
                        We do not record your answers. Submitting just unlocks your recommendation.
                      </p>
                      <button
                        type="button"
                        disabled={!allAnswered}
                        onClick={() => {
                          const t = tierFor(score);
                          setSubmitted(true);
                          trackEvent("readiness_completed", {
                            score,
                            band: t.score,
                            tier: t.tier,
                          });
                          // Fire-and-forget log to backend for the admin dashboard.
                          axios
                            .post(`${API}/events/readiness`, {
                              score,
                              band: t.score,
                              tier: t.tier,
                            })
                            .catch(() => {});
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        data-testid="readiness-submit"
                        className="inline-flex items-center gap-2 bg-stone-900 text-white px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-stone-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        Show my recommendation
                        <ArrowUpRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="result"
                {...STEP_MOTION}
                data-testid="readiness-result"
              >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                  <div className="lg:col-span-7">
                    <Eyebrow>Your read</Eyebrow>
                    <div className="flex items-baseline gap-4">
                      <div className="font-display text-6xl md:text-7xl tracking-tighter font-bold text-stone-900" data-testid="readiness-score">
                        {score}
                      </div>
                      <div className="text-stone-400 font-display text-3xl tracking-tight">/ {maxScore}</div>
                    </div>
                    <div className="mt-4 inline-block border border-[#9C4221]/40 bg-[#9C4221]/[0.06] px-3 py-1.5 text-[11px] tracking-[0.22em] uppercase font-bold text-[#9C4221]" data-testid="readiness-band">
                      {tier.score}
                    </div>
                    <H2 className="text-3xl md:text-4xl mt-8">
                      Recommended pathway: <span className="text-[#9C4221]">{tier.tier}</span>
                    </H2>
                    <P className="mt-6">{tier.summary}</P>

                    <div className="mt-10 flex flex-wrap items-center gap-3">
                      <Link
                        to={tier.tierLink}
                        data-testid="readiness-cta-tier"
                        className="inline-flex items-center gap-2 bg-stone-900 text-white px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-stone-800 transition-colors"
                      >
                        {tier.cta} <ArrowUpRight className="h-4 w-4" />
                      </Link>
                      <Link
                        to="/contact"
                        data-testid="readiness-cta-contact"
                        className="inline-flex items-center gap-2 border border-stone-300 px-6 py-3.5 text-sm font-medium rounded-sm hover:border-stone-900 transition-colors text-stone-900"
                      >
                        Talk to Liana
                      </Link>
                      <button
                        type="button"
                        onClick={handleReset}
                        data-testid="readiness-reset"
                        className="inline-flex items-center gap-2 text-sm text-stone-600 hover:text-stone-900 px-3 py-3.5"
                      >
                        <RotateCcw className="h-3.5 w-3.5" /> Retake
                      </button>
                    </div>
                  </div>

                  <aside className="lg:col-span-5">
                    <div className="border border-stone-200 bg-stone-50 p-6">
                      <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-stone-500 mb-4">
                        Your answers
                      </div>
                      <ul className="space-y-3">
                        {QUESTIONS.map((q) => {
                          const v = answers[q.id];
                          const opt = q.options.find(([, val]) => val === v);
                          return (
                            <li key={q.id} className="text-sm">
                              <div className="text-stone-500 leading-snug">{q.prompt}</div>
                              <div className="mt-1 text-stone-900 font-medium leading-snug">
                                {opt?.[0]} <span className="text-stone-400 text-xs">· {v}/4</span>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                    <p className="mt-5 text-xs text-stone-500 leading-relaxed max-w-sm">
                      This self-assessment is an indicative read, not a substitute for a discovery engagement. Final tier recommendation depends on cultural authority requirements, data sensitivity and delivery risk.
                    </p>
                  </aside>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </Section>
      </section>
    </>
  );
}
